import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { ShoppingCartIcon, ArrowLeftIcon, CheckCircleIcon, Loader2Icon, TrashIcon, InfoIcon } from 'lucide-react';
import { useCart, CartItem } from '../components/cart/CartContext';
import { useAuth } from '../components/auth/AuthProvider';
export const CheckoutPage = () => {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const {
    items,
    totalPrice,
    totalItems,
    removeItem,
    clearCart
  } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  // Snapshot of purchased items (captured before cart is cleared)
  const purchasedItemsRef = useRef<CartItem[]>([]);
  const purchasedTotalRef = useRef(0);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const handleCompletePurchase = () => {
    setIsProcessing(true);
    // Snapshot cart before clearing
    purchasedItemsRef.current = [...items];
    purchasedTotalRef.current = totalPrice;
    // Capture items before the timeout (avoid stale closures)
    const itemsToSave = items.map(item => ({
      id: item.id,
      title: item.title,
      price: item.price,
      image: item.image,
      quantity: item.quantity
    }));
    const totalToSave = totalPrice;
    const currentUserId = user?.id;
    // Simulate processing delay
    setTimeout(() => {
      // Save demo purchase directly to localStorage
      if (currentUserId) {
        const storageKey = `dgstack_demo_purchases_${currentUserId}`;
        const newPurchase = {
          orderId: `ORD-${Date.now().toString(36).toUpperCase()}`,
          items: itemsToSave,
          total: totalToSave,
          date: new Date().toISOString()
        };
        let existing = [];
        try {
          const raw = localStorage.getItem(storageKey);
          if (raw) existing = JSON.parse(raw);
        } catch {}
        localStorage.setItem(storageKey, JSON.stringify([newPurchase, ...existing]));
      }
      clearCart();
      setIsProcessing(false);
      setOrderComplete(true);
    }, 1800);
  };
  // Empty cart state (only when no order was just completed)
  if (items.length === 0 && !orderComplete) {
    return <div className="min-h-screen bg-gray-50 dark:bg-[#0F1F1D] transition-colors duration-300" data-id="element-3980">
        <Navbar scrolled={scrolled} data-id="element-3981" />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-id="element-3982">
          <div className="bg-white dark:bg-[#162825] rounded-xl p-12 text-center shadow-sm" data-id="element-3983">
            <div className="w-16 h-16 bg-gray-100 dark:bg-[#1A3330] rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3984">
              <ShoppingCartIcon className="h-8 w-8 text-gray-400 dark:text-[#7A8F8A]" data-id="element-3985" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-3986">
              Your cart is empty
            </h2>
            <p className="text-gray-500 dark:text-[#7A8F8A] mb-6" data-id="element-3987">
              Add some products to your cart before checking out.
            </p>
            <button onClick={() => navigate('/browse')} className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white rounded-lg font-medium hover:shadow-lg transition-all" data-id="element-3988">
              Browse Products
            </button>
          </div>
        </main>
        <Footer data-id="element-3989" />
      </div>;
  }
  // Order confirmed — demo success screen
  if (orderComplete) {
    const purchasedItems = purchasedItemsRef.current;
    const purchasedTotal = purchasedTotalRef.current;
    return <div className="min-h-screen bg-gray-50 dark:bg-[#0F1F1D] transition-colors duration-300" data-id="element-3990">
        <Navbar scrolled={scrolled} data-id="element-3991" />
        <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24" data-id="element-3992">
          {/* Success card */}
          <div className="bg-white dark:bg-[#162825] rounded-xl p-10 text-center shadow-sm" data-id="element-3993">
            <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-5" data-id="element-3994">
              <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400" data-id="element-3995" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-3996">
              Order Confirmed!
            </h2>
            <p className="text-gray-500 dark:text-[#7A8F8A] mb-8 max-w-md mx-auto" data-id="element-3997">
              Thank you for your purchase. Your order has been placed
              successfully.
            </p>

            {/* Order summary */}
            {purchasedItems.length > 0 && <div className="bg-gray-50 dark:bg-[#0F1F1D] rounded-lg p-5 mb-8 text-left max-w-md mx-auto" data-id="element-3998">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-[#B0C1BD] uppercase tracking-wide mb-3" data-id="element-3999">
                  Order Summary
                </h3>
                <div className="space-y-3" data-id="element-4000">
                  {purchasedItems.map(item => <div key={item.id} className="flex justify-between items-center text-sm" data-id="element-4001">
                      <span className="text-gray-700 dark:text-[#F8FAF9] truncate mr-4" data-id="element-4002">
                        {item.title}
                        {item.quantity > 1 && <span className="text-gray-400 dark:text-[#7A8F8A]" data-id="element-4003">
                            {' '}
                            × {item.quantity}
                          </span>}
                      </span>
                      <span className="text-gray-800 dark:text-[#F8FAF9] font-medium flex-shrink-0" data-id="element-4004">
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>
                    </div>)}
                </div>
                <div className="border-t border-gray-200 dark:border-[#1A3330] mt-3 pt-3 flex justify-between" data-id="element-4005">
                  <span className="text-sm font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-4006">
                    Total
                  </span>
                  <span className="text-sm font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-4007">
                    ${purchasedTotal.toFixed(2)}
                  </span>
                </div>
              </div>}

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-4008">
              <button onClick={() => navigate('/buyer-dashboard?tab=purchases')} className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] text-white rounded-lg font-medium hover:shadow-lg transition-all" data-id="element-4009">
                View My Purchases
              </button>
              <button onClick={() => navigate('/browse')} className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 dark:border-[#1A3330] text-gray-700 dark:text-[#F8FAF9] bg-white dark:bg-transparent rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-[#1A3330] transition-colors" data-id="element-4010">
                Continue Shopping
              </button>
            </div>
          </div>

          {/* Demo info banner */}
          <div className="mt-4 flex items-start gap-3 bg-gray-100 dark:bg-[#162825] rounded-lg px-5 py-4 max-w-3xl mx-auto" data-id="element-4011">
            <InfoIcon className="h-4 w-4 text-gray-400 dark:text-[#7A8F8A] mt-0.5 flex-shrink-0" data-id="element-4012" />
            <p className="text-xs text-gray-500 dark:text-[#7A8F8A] leading-relaxed" data-id="element-4013">
              This is a demo transaction. Connect your preferred payment
              provider (Stripe, Paddle, or others) to enable real payments.
            </p>
          </div>
        </main>
        <Footer data-id="element-4014" />
      </div>;
  }
  // Main checkout view
  return <div className="min-h-screen bg-gray-50 dark:bg-[#0F1F1D] transition-colors duration-300" data-id="element-4015">
      <Navbar scrolled={scrolled} data-id="element-4016" />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-4017">
        {/* Back button */}
        <button onClick={() => navigate(-1)} className="flex items-center text-sm text-gray-500 dark:text-[#7A8F8A] hover:text-gray-700 dark:hover:text-[#B0C1BD] mb-8 transition-colors" data-id="element-4018">
          <ArrowLeftIcon className="h-4 w-4 mr-1" data-id="element-4019" />
          Back
        </button>

        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-8" data-id="element-4020">
          Checkout
        </h1>

        <div className="flex flex-col lg:flex-row gap-8" data-id="element-4021">
          {/* Order Items */}
          <div className="flex-1" data-id="element-4022">
            <div className="bg-white dark:bg-[#162825] rounded-xl shadow-sm overflow-hidden" data-id="element-4023">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1A3330]" data-id="element-4024">
                <h2 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9]" data-id="element-4025">
                  Order Items ({totalItems})
                </h2>
              </div>
              <div className="divide-y divide-gray-100 dark:divide-[#1A3330]" data-id="element-4026">
                {items.map(item => <div key={item.id} className="flex items-center gap-4 px-6 py-4" data-id="element-4027">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100 dark:bg-[#1A3330] flex-shrink-0" data-id="element-4028">
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover" onError={e => {
                    e.currentTarget.src = 'https://images.unsplash.com/photo-1561070791-2526d30994b5?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80';
                  }} data-id="element-4029" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-4030">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate" data-id="element-4031">
                        {item.title}
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-[#7A8F8A] mt-0.5" data-id="element-4032">
                        Qty: {item.quantity}
                      </p>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-4033">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-4034">
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>
                      <button onClick={() => removeItem(item.id)} className="text-gray-400 dark:text-[#7A8F8A] hover:text-red-500 dark:hover:text-red-400 transition-colors" aria-label={`Remove ${item.title}`} data-id="element-4035">
                        <TrashIcon className="h-4 w-4" data-id="element-4036" />
                      </button>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:w-80" data-id="element-4037">
            <div className="bg-white dark:bg-[#162825] rounded-xl shadow-sm p-6 sticky top-24" data-id="element-4038">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-[#F8FAF9] mb-4" data-id="element-4039">
                Order Summary
              </h2>
              <div className="space-y-3 mb-4" data-id="element-4040">
                <div className="flex justify-between text-sm text-gray-600 dark:text-[#B0C1BD]" data-id="element-4041">
                  <span data-id="element-4042">
                    Subtotal ({totalItems} item{totalItems !== 1 ? 's' : ''})
                  </span>
                  <span data-id="element-4043">${totalPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-gray-600 dark:text-[#B0C1BD]" data-id="element-4044">
                  <span data-id="element-4045">Processing fee</span>
                  <span data-id="element-4046">$0.00</span>
                </div>
              </div>
              <div className="border-t border-gray-100 dark:border-[#1A3330] pt-4 mb-6" data-id="element-4047">
                <div className="flex justify-between text-base font-semibold text-gray-900 dark:text-white" data-id="element-4048">
                  <span data-id="element-4049">Total</span>
                  <span data-id="element-4050">${totalPrice.toFixed(2)}</span>
                </div>
              </div>
              <button onClick={handleCompletePurchase} disabled={isProcessing} className={`w-full flex items-center justify-center px-6 py-3 rounded-lg text-base font-medium text-white bg-gradient-to-r from-[#0A3C52] to-[#2DB489] dark:from-[#2DB489] dark:to-[#4ED1A0] hover:shadow-lg transition-all ${isProcessing ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-4051">
                {isProcessing ? <span className="flex items-center" data-id="element-4052">
                    <Loader2Icon className="animate-spin h-5 w-5 mr-2" data-id="element-4053" />
                    Processing your order...
                  </span> : 'Complete Purchase'}
              </button>
              <p className="mt-4 text-xs text-center text-gray-500 dark:text-[#7A8F8A]" data-id="element-4054">
                Secure checkout · All transactions encrypted
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer data-id="element-4055" />
    </div>;
};